import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Ban, Trash2, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from "@/hooks/useAuth";
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AdminUser {
    id: string;
    full_name: string;
    email: string;
    role: 'admin' | 'student';
    status: 'active' | 'suspended';
    created_at: string;
}

const AdminUsers = () => {
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { user: currentUser } = useAuth();

    const loadUsers = async () => {
        setIsLoading(true);
        const data = await safeFetch<AdminUser[]>(
            supabase.from('profiles').select('*').order('created_at', { ascending: false }),
            [],
            'Loading users'
        );
        setUsers(data);
        setIsLoading(false);
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const toggleAccess = async (id: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ status: newStatus })
                .eq('id', id);

            if (error) throw error;

            setUsers(users.map(u => u.id === id ? { ...u, status: newStatus as any } : u));
            toast.info(`User access updated to ${newStatus}`);
        } catch (error: any) {
            toast.error(`Failed to update user status`);
        }
    };

    const deleteUser = async (id: string) => {
        if (id === currentUser?.id) {
            toast.error('You cannot delete your own admin account.');
            setUserToDelete(null);
            return;
        }

        try {
            const { error } = await supabase
                .from('profiles')
                .delete()
                .eq('id', id);

            if (error) throw error;

            setUsers(users.filter(u => u.id !== id));
            toast.success('User deleted successfully');
            setUserToDelete(null);
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Users</h2>
                        <p className="text-slate-500 font-medium text-sm">Manage website users and their access levels here.</p>
                    </div>
                </div>

                <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px] md:min-w-0">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">User Info</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Role</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Joined Date</th>
                                    <th className="px-8 py-4 text-right text-[9px] font-black uppercase tracking-widest text-slate-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan={5} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">Loading users...</td></tr>
                                ) : users.length > 0 ? users.map((u) => (
                                    <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center border border-primary/10">
                                                    <span className="text-[10px] font-black text-primary">{u.full_name?.charAt(0) || 'U'}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-slate-900 uppercase tracking-tight">{u.full_name}</span>
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{u.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${u.status === 'active'
                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                : 'bg-destructive/5 text-destructive border-destructive/10'
                                                }`}>
                                                {u.status}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            {new Date(u.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => toggleAccess(u.id, u.status)}
                                                    className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-primary rounded-xl transition-all"
                                                >
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                                <a href={`mailto:${u.email}`} className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-accent rounded-xl transition-all">
                                                    <Mail className="w-4 h-4" />
                                                </a>
                                                <button
                                                    onClick={() => setUserToDelete(u)}
                                                    className="p-3 bg-slate-50 hover:bg-destructive/10 text-slate-400 hover:text-destructive rounded-xl transition-all"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={5} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">No users found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
                <AlertDialogContent className="rounded-[2rem] border-slate-100 bg-white p-8">
                    <AlertDialogHeader>
                        <div className="w-12 h-12 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4 border border-destructive/20">
                            <AlertTriangle className="w-6 h-6 text-destructive" />
                        </div>
                        <AlertDialogTitle className="text-xl font-black uppercase tracking-tight text-slate-900">Confirm Deletion</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 font-medium text-sm pt-2">
                            Are you sure you want to remove <span className="font-bold text-slate-900">"{userToDelete?.full_name}"</span>? This action is permanent and will restrict all future access.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-8 gap-3">
                        <AlertDialogCancel className="h-14 rounded-2xl px-8 text-[10px] font-black uppercase tracking-[0.2em] border-slate-100 hover:bg-slate-50">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => userToDelete && deleteUser(userToDelete.id)}
                            className="bg-destructive hover:bg-destructive/90 text-white h-14 rounded-2xl px-8 text-[10px] font-black uppercase tracking-[0.2em] border-0 shadow-premium"
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
};

export default AdminUsers;
